import { NavGroupId } from 'enums';
import { MenuItem } from 'interfaces';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelectorShallow } from 'store/hooks';
import { sideNavigationSelector } from 'store/reducer/setting/selectors';
import CollapsedNavRail from './CollapsedNavRail';
import SideNavDivider from './SideNavDivider';
import SideNavGroup from './SideNavGroup';
import SideNavItem from './SideNavItem';
import {
  buildNavGroups,
  findActiveGroupId,
  groupContainsPath,
  NavGroup,
  persistCollapsedGroups,
  readCollapsedGroups
} from 'utils/navBar/sideNav.util';

interface SideNavProps {
  myCircleSubMenus: MenuItem[];
  extraMenuSubMenus: MenuItem[];
  isCollapsed: boolean;
}

const SideNav = ({
  myCircleSubMenus,
  extraMenuSubMenus,
  isCollapsed
}: SideNavProps) => {
  const { pathname } = useLocation();
  const { isUserAdmin, selectedDay } = useAppSelectorShallow(
    sideNavigationSelector
  );

  const [collapsedGroups, setCollapsedGroups] =
    useState<NavGroupId[]>(readCollapsedGroups);

  useEffect(() => {
    persistCollapsedGroups(collapsedGroups);
  }, [collapsedGroups]);

  const { navGroups, adminItem } = useMemo(
    (): { navGroups: NavGroup[]; adminItem: MenuItem | null } =>
      buildNavGroups({
        selectedDay,
        myCircleSubMenus,
        extraMenuSubMenus,
        isUserAdmin
      }),
    [isUserAdmin, extraMenuSubMenus, myCircleSubMenus, selectedDay]
  );

  useEffect(() => {
    const activeGroupId = findActiveGroupId(navGroups, pathname);
    if (!activeGroupId) return;
    setCollapsedGroups((prev) =>
      prev.includes(activeGroupId)
        ? prev.filter((groupId) => groupId !== activeGroupId)
        : prev
    );
  }, [pathname, navGroups]);

  const toggleGroup = useCallback((id: NavGroupId) => {
    setCollapsedGroups((prev) =>
      prev.includes(id)
        ? prev.filter((groupId) => groupId !== id)
        : [...prev, id]
    );
  }, []);

  const renderSubMenuStyle = useCallback((subPath?: string) => {
    const childPath = subPath && window.location.pathname.endsWith(subPath);
    return `~text-[0.7rem]/sm ~px-2/3 ~py-1/2 data-[state=open]:bg-primary-100 ${
      childPath
        ? 'bg-primary-200 hover:bg-primary-200 text-gray-900 hover:text-gray-900'
        : 'text-gray-700 dark:text-secondary-100 hover:bg-primary-100 dark:hover:bg-secondary-dark-300 bg-primary-50 dark:bg-secondary-dark-200'
    }`;
  }, []);

  if (isCollapsed) {
    return (
      <nav className='flex-1 w-full overflow-y-auto scrollbar-thin-custom py-4 ~px-2/3 ~space-y-6/4'>
        <CollapsedNavRail
          navGroups={navGroups}
          adminItem={adminItem}
          renderSubMenuStyle={renderSubMenuStyle}
        />
      </nav>
    );
  }

  return (
    <nav className='flex-1 w-full overflow-y-auto scrollbar-thin-custom py-4 ~px-2/3 ~space-y-4/3'>
      {navGroups.map((group, index) => {
        const isExpanded = !collapsedGroups.includes(group.id);
        return (
          <Fragment key={group.id}>
            {index > 0 && <SideNavDivider id={`divider-${group.id}`} />}
            <SideNavGroup
              groupId={group.id}
              label={group.label}
              items={group.panelItems}
              isExpanded={isExpanded}
              hasActiveChild={groupContainsPath(group, pathname)}
              onToggle={() => toggleGroup(group.id)}
            />
          </Fragment>
        );
      })}
      {adminItem && (
        <>
          <SideNavDivider id='divider-admin' />
          <SideNavItem
            text={adminItem.text ?? undefined}
            path={adminItem.path}
            icon={adminItem.icon}
            attribAction={adminItem.attribAction}
            isCollapsed={false}
          />
        </>
      )}
    </nav>
  );
};

export default SideNav;
